import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  OverlayView,
} from '@react-google-maps/api';

import mapStyles from '../../map-styles.json';
import DealerType from '../../types/DealerType';

interface Props {
  center?: google.maps.LatLng | google.maps.LatLngLiteral;
  dealers: Array<DealerType>;
  selectedDealer?: DealerType;
  zoom?: number;
  fitBounds?: boolean;
}

const containerStyle = {
  width: '100%',
  height: '100%',
};

const overlayStyle = {
  backgroundColor: '#fff',
  transform: 'translate(-50%,-100%) translateY(-40px)',
  padding: 5,
  borderRadius: 5,
};

const DealersMap: React.FC<Props> = ({
  center,
  dealers = [],
  zoom = 12,
  fitBounds = false,
  selectedDealer: defaultDealer,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY ?? '',
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const router = useRouter();
  const [selectedDealer, setSelectedDealer] = useState<DealerType>();

  useEffect(() => {
    setSelectedDealer(defaultDealer);
  }, [defaultDealer]);

  const onLoad = useCallback(
    function callback(map: google.maps.Map) {
      if (fitBounds) {
        if (dealers.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          dealers.forEach((dealer) =>
            bounds.extend({ lat: dealer.latitude, lng: dealer.longitude })
          );
          map.fitBounds(bounds);
        }
      } else if (center) {
        map.setCenter(center);
      }
      setMap(map);
    },
    [center, fitBounds, dealers]
  );

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  const smallSize = () => new google.maps.Size(20, 25);
  const largeSize = () => new google.maps.Size(20 * 1.5, 25 * 1.5);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={zoom}
      options={{ styles: mapStyles }}
      onLoad={onLoad}
      onUnmount={onUnmount}
    >
      {dealers.map((dealer, inx) => (
        <Marker
          key={inx}
          position={{ lat: dealer.latitude, lng: dealer.longitude }}
          icon={{
            url: '/images/map-marker.svg',
            scaledSize:
              dealer.id === selectedDealer?.id ? largeSize() : smallSize(),
          }}
          onClick={() => router.push(`/dystrybutorzy/${dealer.slug}`)}
          onMouseOver={() => setSelectedDealer(dealer)}
          onMouseOut={() => setSelectedDealer(defaultDealer)}
        />
      ))}
      {selectedDealer ? (
        <OverlayView
          position={{
            lat: selectedDealer.latitude,
            lng: selectedDealer.longitude,
          }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div style={overlayStyle}>{selectedDealer.name}</div>
        </OverlayView>
      ) : null}
    </GoogleMap>
  ) : (
    <></>
  );
};

export default DealersMap;
